import React from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { FieldInput } from '../../../../ui/FieldInput';

export type StudentFormRow = {
  id: string;
  full_name: string;
  email: string;
};

export type StudentRowErrors = {
  full_name?: string;
  email?: string;
};

export interface StudentCreateFormProps {
  rows: StudentFormRow[];
  errors: Record<string, StudentRowErrors>;
  onRowChange: (rowId: string, field: 'full_name' | 'email', value: string) => void;
  onAddRow: () => void;
  onRemoveRow: (rowId: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
}

export const StudentCreateForm: React.FC<StudentCreateFormProps> = ({
  rows,
  errors,
  onRowChange,
  onAddRow,
  onRemoveRow,
  onSubmit,
  onClose,
}) => {
  const isFormValid = rows.some(row => {
    const rowErrors = errors[row.id] || {};
    return row.full_name.trim() && !rowErrors.email;
  });

  const hasAnyError = rows.some(row => {
    const rowErrors = errors[row.id] || {};
    return rowErrors.full_name || rowErrors.email;
  });

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-lg">
      <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center p-1.5 rounded-md text-gray-500 hover:bg-gray-100 focus:outline-none"
          >
            <ArrowBackIcon />
          </button>
          <h2 className="text-lg font-semibold text-gray-900">Форма создания студентов</h2>
        </div>
      </div>

      <form onSubmit={onSubmit} noValidate>
        <div className="p-4 sm:p-6 space-y-4">
          {/* Заголовки колонок */}
          <div className="hidden sm:grid sm:grid-cols-[1fr_1fr_40px] gap-3 text-sm font-medium text-gray-700 px-1">
            <span>ФИО <span className="text-red-500">*</span></span>
            <span>Email</span>
            <span></span>
          </div>

          {/* Строки студентов */}
          <div className="space-y-3">
            {rows.map((row, index) => {
              const rowErrors = errors[row.id] || {};

              return (
                <div key={row.id} className="space-y-2">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                    {/* ФИО */}
                    <div className="flex-1">
                      <label className="sm:hidden block text-sm font-medium text-gray-700 mb-1">
                        ФИО <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={row.full_name}
                        onChange={(e) => onRowChange(row.id, 'full_name', e.target.value)}
                        placeholder="Иванов Иван Иванович"
                        className={`w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          rowErrors.full_name
                            ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                            : 'border-gray-300 focus:border-blue-500'
                        }`}
                      />
                      {rowErrors.full_name && (
                        <p className="mt-1 text-xs text-red-600">{rowErrors.full_name}</p>
                      )}
                    </div>

                    {/* Email */}
                    <div className="flex-1">
                      <label className="sm:hidden block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        value={row.email}
                        onChange={(e) => onRowChange(row.id, 'email', e.target.value)}
                        placeholder="email@example.com"
                        className={`w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          rowErrors.email
                            ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                            : 'border-gray-300 focus:border-blue-500'
                        }`}
                      />
                      {rowErrors.email && (
                        <p className="mt-1 text-xs text-red-600">{rowErrors.email}</p>
                      )}
                    </div>

                    {/* Кнопка удаления */}
                    <div className="flex items-center sm:pt-0 pt-1">
                      {rows.length > 1 && (
                        <button
                          type="button"
                          onClick={() => onRemoveRow(row.id)}
                          className="inline-flex items-center justify-center w-9 h-9 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 focus:outline-none transition-colors"
                          title="Удалить строку"
                        >
                          <CloseIcon fontSize="small" />
                        </button>
                      )}
                      {rows.length === 1 && <div className="w-9 h-9" />}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Кнопка "Добавить ещё" */}
          <button
            type="button"
            onClick={onAddRow}
            className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 focus:outline-none transition-colors"
          >
            <AddIcon fontSize="small" />
            Добавить ещё
          </button>
        </div>

        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-b-lg border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none"
          >
            Отмена
          </button>
          <button
            type="submit"
            disabled={!isFormValid || hasAnyError}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            Создать студентов
          </button>
        </div>
      </form>
    </div>
  );
};